import { Divider, Grid, LinearProgress, Paper } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { DashboardEventTypes, track } from '~/src/dash/analytics/analytics';
import { DateGroup, getDateGroupNumber, getValidDateGroup } from '~/src/dash/api/console-api';
import {
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { IProps } from '~/src/dash/components/model-dialog-active-view';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import { IMenuList } from '~/src/dash/types/menuList';
import { IBaseState } from '~/src/dash/types/props';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  DateGroupDays,
  DateGroupHours,
  DateGroupMonths,
  DateGroupWeeks,
  formatDateStartDay,
  formatDayMonthName,
  formatHourDayMonth,
  getStartDate,
} from '~/src/dash/utils/date';
import { decorateV2 } from '~/src/dash/utils/decorator';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import {
  assignDateLabel,
  blankMeasurementDataItem,
  combineDeviceTypeFilters,
  dateMatches,
  findCompareEndDate,
  findCompareStartDate,
  generateCalendarProps,
  generateCompareCalendarProps,
  IMeasurementDataItem,
} from '~/src/dash/views/common/ias-data-consts';
import {
  IIASFirstAndThirdPartyCompareDataKey,
  IIASFirstAndThirdPartyDataKey,
} from '~/src/dash/views/common/ias-data-types';
import { iasDashboardConfig } from '~/src/dash/views/ias-publisher/config';
import {
  IIASDashboardDisplayedCardKeyAndColor,
  IIASDashboardFilter,
  IIASDashboardMetricInfo,
  IIASDashboardMetricRateElements,
  IIASDashboardMetricsCard,
  IIASDashboardQueryParams,
  IIASDashboardSectionCardMetricInfo,
  IIASDashboardSectionInfo,
  IIASDashboardSelectableCardMetricInfo,
} from '~/src/dash/views/ias-publisher/IASDashboard/common/types';
import { IASDashboardCards } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardCards';
import { IASDashboardCharts } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardCharts';
import { IASDashboardFilters } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardFilters';
import { IASDashboardSelectableCards } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardSelectableCards';
import { IASDashboardTable } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardTable';
import { IASDashboardTabs } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardTabs';
import {
  generateSnackbarState,
  ISnackbarState,
  SnackbarView,
} from '~/src/dash/views/snackbar-view';

// get config variables
const cfg = iasDashboardConfig;

// creates the labels array for the mui tabs prop
const extractLabels = (sections: Record<string, IIASDashboardSectionInfo>): string[] => {
  const clonedSections = cloneDeep(sections);
  // this sort may not be needed but is a failsafe
  Object.values(clonedSections).sort((a, b) => a.tabIndex - b.tabIndex);
  const labels: string[] = [];
  Object.values(clonedSections).forEach((section) => labels.push(section.label));
  return labels;
};

const getSectionInfo = (tabNumber: number, tabIndex: Record<number, string>) => {
  const key = tabIndex[tabNumber] ? tabIndex[tabNumber] : tabIndex[0];
  return cfg.sections[key];
};

const getAllSelectableCardMetricsInfo = (
  tabNumber: number,
  tabIndex: Record<number, string>,
): IIASDashboardSelectableCardMetricInfo[] => {
  const key = tabIndex[tabNumber] ? tabIndex[tabNumber] : tabIndex[0];
  const allMetrics = cfg.sections[key].cardMetrics.map((cardMetric) => {
    const metric = cfg.metrics[cardMetric.key];
    return {
      key: cardMetric.key,
      dataKey: metric.dataKey,
      label: metric.label,
    };
  });
  return allMetrics;
};

const extractCardMetricsKeys = (cardMetrics: IIASDashboardSectionCardMetricInfo[]) => {
  const metricKeys: string[] = [];
  cardMetrics.forEach((metric) => metricKeys.push(metric.key));
  return metricKeys;
};

const extractMetrics = (
  cardMetrics: IIASDashboardSectionCardMetricInfo[],
  allMetrics: Record<string, IIASDashboardMetricInfo>,
): Record<string, IIASDashboardMetricInfo> => {
  const uniqueMetricKeys = {} as Record<string, boolean>;
  const metricKeys = extractCardMetricsKeys(cardMetrics);
  metricKeys.forEach((key) => {
    if (allMetrics[key]) {
      uniqueMetricKeys[key] = true;
      if (
        allMetrics[key].relatedMetricsKeys &&
        (allMetrics[key].relatedMetricsKeys || []).length > 0
      ) {
        (allMetrics[key].relatedMetricsKeys || []).forEach((relatedKey) => {
          uniqueMetricKeys[relatedKey] = true;
        });
      }
    }
  });
  const updatedKeys = [...metricKeys, ...Object.keys(uniqueMetricKeys)];
  const selectedMetrics = {} as Record<string, IIASDashboardMetricInfo>;
  updatedKeys.forEach((metricKey) => (selectedMetrics[metricKey] = allMetrics[metricKey]));
  return selectedMetrics;
};

const generateActiveMetrics = (
  cardMetrics: IIASDashboardSectionCardMetricInfo[],
  metrics: Record<string, IIASDashboardMetricInfo>,
) => {
  const activeMetrics = {} as Record<string, boolean>;
  cardMetrics.map((metric) => {
    const { key, isDefaultActive } = metric;
    activeMetrics[metrics[key].dataKey] = isDefaultActive;
  });
  return activeMetrics;
};

const generateTotals = (
  sectionMetrics: Record<string, IIASDashboardMetricInfo>,
  data: IMeasurementDataItem[],
  isComparedToEnabled: boolean,
) => {
  // gets necessary data sums
  // first get the keys
  const newTotals = {} as Record<string, number>;
  Object.values(sectionMetrics).map((metric) => {
    if (metric.dataType === 'integer') {
      newTotals[metric.dataKey] = 0;
      if (isComparedToEnabled) {
        newTotals[metric.compareKey] = 0;
      }
    }
  });
  // then get sums
  data.map((group: IMeasurementDataItem) => {
    Object.keys(newTotals).forEach((elementKey: IIASFirstAndThirdPartyDataKey) => {
      const val = group[elementKey];
      newTotals[elementKey] += val && typeof val === 'number' ? val : 0;
    });
  });
  return newTotals;
};

// used in generateCard and generateCards
const getCompareKey = (key: string): string => {
  return `Compare${key}`;
};

const getPercentChange = (value: number, comparedValue: number) => {
  return 100 * ((value - comparedValue) / comparedValue);
};

interface IGetCardValuesParams {
  dataKey: string;
  calcType: string;
  rateElements: IIASDashboardMetricRateElements;
  isComparedToEnabled: boolean;
  data: Record<string, number>;
}

const getCardValues = ({
  dataKey,
  calcType,
  rateElements,
  isComparedToEnabled,
  data,
}: IGetCardValuesParams) => {
  let value = 'N/A';
  let percentChange = '';
  let isPercentChangeNegative = false;
  switch (calcType) {
    case 'sum':
      const sum = data[dataKey] || 0;
      value = convertLargeNumbersToRoundedString(sum);
      if (isComparedToEnabled) {
        const compareSum = data[getCompareKey(dataKey)];
        const sumChange = getPercentChange(sum, compareSum);
        if (sumChange < 0) {
          isPercentChangeNegative = true;
        }
        percentChange = `${sumChange.toFixed(2)}%`;
      }
      break;
    case 'rate':
      if (rateElements) {
        let sumNumerators = 0;
        let sumCompareNumerators = 0;
        rateElements.numeratorKeys.map((key) => {
          sumNumerators += data[key];
          if (isComparedToEnabled) {
            sumCompareNumerators += data[getCompareKey(key)];
          }
        });
        let sumDenominators = 0;
        let sumCompareDenominators = 0;
        rateElements.denominatorKeys.map((key) => {
          sumDenominators += data[key];
          if (isComparedToEnabled) {
            sumCompareDenominators += data[getCompareKey(key)];
          }
        });
        const avgRate = sumNumerators / sumDenominators;
        value = `${(avgRate * 100).toFixed(2)}%`;
        if (isComparedToEnabled) {
          const compareAvgRate = sumCompareNumerators / sumCompareDenominators;
          const avgRateChange = getPercentChange(avgRate, compareAvgRate);
          if (avgRateChange < 0) {
            isPercentChangeNegative = true;
          }
          percentChange = `${avgRateChange.toFixed(2)}%`;
        }
        break;
      }
    default:
      break;
  }
  return { value, percentChange, isPercentChangeNegative };
};

const generateDisplayedCardMetrics = (
  cardMetrics: IIASDashboardSectionCardMetricInfo[],
  areCardsSelectable: boolean | undefined,
) => {
  let displayedCardMetrics = cardMetrics;
  if (areCardsSelectable) {
    displayedCardMetrics = cardMetrics.filter((cardMetric) => cardMetric.isDisplayed === true);
  }
  return displayedCardMetrics;
};

const generateCard = (
  metrics: Record<string, IIASDashboardMetricInfo>,
  totals: Record<string, number>,
  isComparedToEnabled: boolean,
  colorBank: string[],
  key: string,
  index: number,
) => {
  const metric = metrics[key];
  const isPercentage = metric.yAxisID === 'rate';
  const values = getCardValues({
    dataKey: metric.dataKey,
    calcType: metric.calcType || '',
    rateElements: metric.rateElements || ({} as IIASDashboardMetricRateElements),
    isComparedToEnabled,
    data: totals,
  });
  return {
    keyString: key,
    label: metric.label,
    dataKey: metric.dataKey,
    color: colorBank[index % colorBank.length],
    isPercentage,
    isInverseMetric: metric.isInverseMetric,
    value: values.value,
    percentChange: values.percentChange,
    isPercentChangeNegative: values.isPercentChangeNegative,
    isActive: true,
  } as IIASDashboardMetricsCard;
};

const generateCards = (
  cardMetrics: IIASDashboardSectionCardMetricInfo[],
  metrics: Record<string, IIASDashboardMetricInfo>,
  totals: Record<string, number>,
  isComparedToEnabled: boolean,
  currentSection: IIASDashboardSectionInfo,
  colorBank: string[],
  cards: IIASDashboardMetricsCard[],
) => {
  const currCards = [] as IIASDashboardMetricsCard[];
  const keys = Object.keys(metrics);

  const displayedCardMetrics = generateDisplayedCardMetrics(
    cardMetrics,
    currentSection.areCardsSelectable,
  );

  // create array of card objects
  const metricKeys = extractCardMetricsKeys(displayedCardMetrics);

  metricKeys.map((key, i) => {
    const metric = metrics[key];
    const isPercentage = metric.yAxisID === 'rate';
    const values = getCardValues({
      dataKey: metric.dataKey,
      calcType: metric.calcType || '',
      rateElements: metric.rateElements || ({} as IIASDashboardMetricRateElements),
      isComparedToEnabled,
      data: totals,
    });
    currentSection.areCardsSelectable
      ? cards.length > 0
        ? currCards.push({
            keyString: key,
            label: metric.label,
            dataKey: metric.dataKey,
            color: colorBank[i % colorBank.length],
            isPercentage,
            isInverseMetric: metric.isInverseMetric,
            value: values.value,
            percentChange: values.percentChange,
            isPercentChangeNegative: values.isPercentChangeNegative,
            isActive: cards[i].isActive,
          } as IIASDashboardMetricsCard)
        : currCards.push({
            keyString: key,
            label: metric.label,
            dataKey: metric.dataKey,
            color: colorBank[i % colorBank.length],
            isPercentage,
            isInverseMetric: metric.isInverseMetric,
            value: values.value,
            percentChange: values.percentChange,
            isPercentChangeNegative: values.isPercentChangeNegative,
            isActive: displayedCardMetrics[i].isDefaultActive,
          } as IIASDashboardMetricsCard)
      : currCards.push({
          keyString: keys[i],
          label: metric.label,
          dataKey: metric.dataKey,
          color: metric.color,
          isPercentage,
          isInverseMetric: metric.isInverseMetric,
          value: values.value,
          percentChange: values.percentChange,
          isPercentChangeNegative: values.isPercentChangeNegative,
          isActive: cardMetrics[i].isDefaultActive,
        } as IIASDashboardMetricsCard);
  });
  return currCards;
};

interface IIASDashboardProps extends IStore, IBaseState, IProps {}

const _IASDashboard = ({ classes, location, session, user }: IIASDashboardProps) => {
  const isMounted = useRef(false);

  const [currentTab, setCurrentTab] = useState(0);
  const [currentSection, setCurrentSection] = useState(getSectionInfo(currentTab, cfg.tabIndex));

  // loading state
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [data, setData] = useState<any>(null);
  const [mergedData, setMergedData] = useState<IMeasurementDataItem[]>([]);

  const [sectionMetrics, setSectionMetrics] = useState<Record<string, IIASDashboardMetricInfo>>(
    extractMetrics(currentSection.cardMetrics, cfg.metrics),
  );

  const [activeMetrics, setActiveMetrics] = useState(
    generateActiveMetrics(currentSection.cardMetrics, cfg.metrics),
  );

  const [handleType, setHandleType] = useState('');

  // filter state
  const [filters, setFilters] = useState<Record<string, IIASDashboardFilter>>(cfg.filters);

  // cards state
  const [cardsLoadState, setCardsLoadState] = useState(LoadingStateLoading);
  const [cards, setCards] = useState<IIASDashboardMetricsCard[]>([]);
  const [displayedCardsKeysAndColors, setDisplayedCardsKeysAndColors] = useState(
    [] as IIASDashboardDisplayedCardKeyAndColor[],
  );

  const [totals, setTotals] = useState({} as Record<string, number>);

  // calendar state
  const [calendar, setCalendar] = useState(generateCalendarProps(cfg.timezone));
  const [calFocusedInput, setCalFocusedInput] = useState<IFocusedInput>(null);
  const [compareCalendar, setCompareCalendar] = useState(
    generateCompareCalendarProps(cfg.timezone),
  );
  const [compFocusedInput, setCompFocusedInput] = useState<IFocusedInput>(null);
  const [densityRangeAgg, setDensityRangeAgg] = useState(DateGroupDays);
  const [isComparedToEnabled, setIsComparedToEnabled] = useState(true);
  const [rangeSelectedStr, setRangeSelectedStr] = useState('Last 7 Days');

  // chart state -- calendar/compare calendar data
  const [chartLoadState, setChartLoadState] = useState(LoadingStateLoading);
  const [compChartLoadState, setCompChartLoadState] = useState(LoadingStateLoading);

  // additional charts api params
  const [queryParams, setQueryParams] = useState<IIASDashboardQueryParams>(
    {} as IIASDashboardQueryParams,
  );

  // snackbar state
  const [snackbar, setSnackbar] = useState<ISnackbarState>(generateSnackbarState());

  // colorbank
  const colorBank = Object.values(cfg.colors);

  const { pathname } = location;

  // run the api call to get all the data on mount
  useEffect(() => {
    // we send empty object here to default to use only state variables
    trackEvent('IAS Publisher Dashboard - View');
    runAggregateQuery({});
  }, []);

  // everytime the tab changes, we reset the following states and set load states to loading
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setCurrentSection(getSectionInfo(currentTab, cfg.tabIndex));
        setCards([]);
        setMergedData([]);
        setLoadState(LoadingStateLoading);
        setCardsLoadState(LoadingStateLoading);
        setChartLoadState(LoadingStateLoading);
        setCompChartLoadState(LoadingStateLoading);
      });
    }
  }, [currentTab]);

  // everytime we change sections, we get the correct associated data
  useEffect(() => {
    if (isMounted.current) {
      unstable_batchedUpdates(() => {
        setSectionMetrics(extractMetrics(currentSection.cardMetrics, cfg.metrics));
        setActiveMetrics(generateActiveMetrics(currentSection.cardMetrics, cfg.metrics));
      });
      trackEvent(
        `IAS Publisher Dashboard - Click ${currentSection.label} Tab` as DashboardEventTypes,
      );
    }
  }, [currentSection]);

  // refresh when data changes
  useEffect(() => {
    if (isMounted.current) {
      if (data && data.length !== 0) {
        setLoadState(LoadingStateHasData);

        const allData = [] as any;
        allData.push(data[0]);
        allData.push(data[1]);
        const configuredData = configureData(allData);
        setMergedData(configuredData);
      }
    }
  }, [data]);

  // whenever the section metrics, data, or comparison toggle changes, we set the associated cards
  useEffect(() => {
    if (isMounted.current) {
      setCardsLoadState(LoadingStateLoading);
      if (mergedData.length !== 0) {
        setTotals(generateTotals(sectionMetrics, mergedData, isComparedToEnabled));
      }
    }
  }, [sectionMetrics, mergedData, isComparedToEnabled]);

  useEffect(() => {
    if (isMounted.current) {
      setCards(
        generateCards(
          currentSection.cardMetrics,
          sectionMetrics,
          totals,
          isComparedToEnabled,
          currentSection,
          colorBank,
          cards,
        ),
      );
    }
  }, [totals]);

  useEffect(() => {
    const newDisplayedCardsKeysAndColors: IIASDashboardDisplayedCardKeyAndColor[] = [];
    cards.forEach((card, index) =>
      newDisplayedCardsKeysAndColors.push({
        dataKey: card.dataKey,
        color: colorBank[index % colorBank.length],
      }),
    );
    setDisplayedCardsKeysAndColors(newDisplayedCardsKeysAndColors);
  }, [cards]);

  // whenever the cards change, we change the card load state
  useEffect(() => {
    if (isMounted.current) {
      setCardsLoadState(cards ? LoadingStateHasData : LoadingStateNoData);
    }
  }, [cards]);

  // handles calendar changes
  useEffect(() => {
    if (
      isMounted.current &&
      handleType !== 'handleDensityRangeChangeAgg' &&
      handleType !== 'handleCalendarFocusedInputChange'
    ) {
      const updatedCompStartDate = findCompareStartDate({
        timezone: cfg.timezone,
        startDate: calendar.displayStartDate,
        endDate: calendar.displayEndDate,
        rangeSelected: rangeSelectedStr,
      });
      const updatedCompEndDate = findCompareEndDate({
        timezone: cfg.timezone,
        startDate: calendar.displayStartDate,
        endDate: calendar.displayEndDate,
        rangeSelected: rangeSelectedStr,
      });
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      clonedCompareCalendar.startDate = updatedCompStartDate;
      clonedCompareCalendar.endDate = updatedCompEndDate;
      clonedCompareCalendar.displayStartDate = updatedCompStartDate;
      clonedCompareCalendar.displayEndDate = updatedCompEndDate;
      setCompareCalendar(clonedCompareCalendar);
    } else {
      isMounted.current = true;
    }
  }, [calendar, densityRangeAgg]);

  // whenever any of the following dependencies change, but not via the following handlers, we re-run the api call with the new params
  useEffect(() => {
    if (
      isMounted.current &&
      handleType !== 'handleFilterOnChange' &&
      handleType !== 'handleCalendarDateChange' &&
      handleType !== 'handleCalendarFocusedInputChange' &&
      handleType !== 'handleChangeAggRangeSelected'
    ) {
      runAggregateQuery({});
    }
  }, [currentSection, calendar, compareCalendar, densityRangeAgg, filters]);

  // Notes: This prevents an unnecessary api call when disabling comparison data
  useEffect(() => {
    if (isMounted.current && handleType === 'handleCompareEnabled' && isComparedToEnabled) {
      runAggregateQuery({});
    }
  }, [isComparedToEnabled]);

  const trackEvent = (
    eventTitle: DashboardEventTypes,
    graph_id = 0,
    start_date = '',
    end_date = '',
    comp_start_date = '',
    comp_end_date = '',
    timezone = '',
    time_to_load_sec = 0,
    query_failed = false,
    query_duration = 0,
    comp_query_duration_days = 0,
    compare_enabled = false,
  ) => {
    const is_super_user = hasRoleSuperUser(user.user);
    if (
      eventTitle === 'IAS Publisher Dashboard - Open Calendar' ||
      eventTitle === 'IAS Publisher Dashboard - View' ||
      eventTitle === 'IAS Publisher Dashboard - Open Filter Pill' ||
      eventTitle === 'IAS Publisher Dashboard - Open Compare Calendar'
    ) {
      track(
        eventTitle,
        {
          is_super_user,
        },
        user.user,
      );
    } else if (eventTitle === 'IAS Publisher Dashboard - Compare Enabled') {
      track(
        eventTitle,
        {
          is_super_user,
          compare_enabled: isComparedToEnabled,
        },
        user.user,
      );
    } else if (eventTitle === 'IAS Publisher Dashboard - Run Agg Query') {
      track(
        eventTitle,
        {
          is_super_user,
          graph_id,
          start_date,
          end_date,
          comp_start_date,
          comp_end_date,
          timezone,
          time_to_load_sec,
          query_failed,
          query_duration_days: query_duration,
          comp_query_duration_days,
          compare_enabled,
        },
        user.user,
      );
    }
  };

  interface IRunAggregateQueryParams {
    startDate?: moment.Moment | null;
    endDate?: moment.Moment | null;
    comparedStartDate?: moment.Moment | null;
    comparedEndDate?: moment.Moment | null;
  }

  const runAggregateQuery = ({
    startDate = null,
    endDate = null,
    comparedStartDate = null,
    comparedEndDate = null,
  }: IRunAggregateQueryParams): void => {
    // reset load states
    unstable_batchedUpdates(() => {
      setLoadState(LoadingStateLoading);
      setCardsLoadState(LoadingStateLoading);
      setChartLoadState(LoadingStateLoading);
      setCompChartLoadState(LoadingStateLoading);
    });
    // query 1 (normal calendar)
    const start_date = formatDateStartDay(startDate || calendar.startDate);
    const end_date = (endDate || calendar.endDate).format('YYYY-MM-DDTHH:mm:ss');
    const query_duration_days = calendar.endDate.diff(calendar.startDate, 'days') + 1;
    // query 2 (compare calendar)
    const comp_start_date = formatDateStartDay(comparedStartDate || compareCalendar.startDate);
    const comp_end_date = (comparedEndDate || compareCalendar.endDate).format(
      'YYYY-MM-DDTHH:mm:ss',
    );
    const comp_query_duration_days =
      compareCalendar.endDate.diff(compareCalendar.startDate, 'days') + 1;
    // query params
    const graph_id = currentSection.graph;
    const grouping = densityRangeAgg;

    // get devicetypes
    const deviceTypeFilters = {
      Formats: filters.Formats,
      'Device Types': filters['Device Types'],
    };
    const combinedDeviceTypes = Object.keys(filters).includes('Formats')
      ? combineDeviceTypeFilters(deviceTypeFilters)
      : [];
    const groupingNumber = getDateGroupNumber(grouping);
    const teamID = '15661';
    const publisherID = session.publisher.ID;
    const anIDs = [+session.publisher.IASAnID];

    // save new request params for other charts
    setQueryParams({
      startDate: start_date,
      endDate: end_date,
      compStartDate: comp_start_date,
      compEndDate: comp_end_date,
      timezone: cfg.timezone,
      groupingNumber,
      deviceTypes: combinedDeviceTypes,
      teamID,
      anIDs,
      publisherID,
    });

    const body1 = {
      GraphID: graph_id,
      StartDate: start_date,
      EndDate: end_date,
      Timezone: cfg.timezone,
      GroupingNumber: groupingNumber,
      DeviceTypes: combinedDeviceTypes,
      TeamID: teamID,
      AnIDs: anIDs,
    } as IIASFirstAndThirdPartyRequestBody;
    const body2 = {
      GraphID: graph_id,
      StartDate: comp_start_date,
      EndDate: comp_end_date,
      Timezone: cfg.timezone,
      GroupingNumber: groupingNumber,
      DeviceTypes: combinedDeviceTypes,
      TeamID: teamID,
      AnIDs: anIDs,
    } as IIASFirstAndThirdPartyRequestBody;

    /** * Main Graph Queries ***/
    const queries = [
      queryFirstAndThirdPartyIasData(body1, { publisher_id: publisherID }),
      queryFirstAndThirdPartyIasData(body2, { publisher_id: publisherID }),
    ];

    // Runs the queries together in Promise All
    const queryStartTime = moment().unix();
    Promise.all(queries)
      .then((data: any[][]) => {
        const queryTotalTime = moment().unix() - queryStartTime;
        let queryFailed = false;

        // if the call is successful but there is no data
        if (!data[0][0].rows) {
          queryFailed = true;
          unstable_batchedUpdates(() => {
            setLoadState(LoadingStateNoData);
            setCardsLoadState(LoadingStateNoData);
            setChartLoadState(LoadingStateNoData);
            setCompChartLoadState(LoadingStateNoData);
            setSnackbar({
              message: `We are not tracking any measurement data here...\nPlease adjust the filters / date range combination`,
              warning: true,
              error: false,
            });
          });
          return;
        }

        const data1 = [] as any;
        data1.push(data[0][0].rows);
        data1.push(data[1][0].rows);

        // check the data to make sure it's there, otherwise show an error msg
        if (!data1[0].rows) {
          if (data1[0].length === 0) {
            queryFailed = true;
            if (
              !isComparedToEnabled &&
              formatDateStartDay(calendar.endDate) === formatDateStartDay(calendar.startDate)
            ) {
              unstable_batchedUpdates(() => {
                setLoadState(LoadingStateNoData);
                setCardsLoadState(LoadingStateNoData);
                setChartLoadState(LoadingStateNoData);
                setCompChartLoadState(LoadingStateNoData);
              });
            } else {
              unstable_batchedUpdates(() => {
                setLoadState(LoadingStateNoData);
                setCardsLoadState(LoadingStateNoData);
                setChartLoadState(LoadingStateNoData);
                setCompChartLoadState(LoadingStateNoData);
              });
            }
            // if there is no compare data (query went wrong or publisher is new), reload the graph without compare
          } else if (isComparedToEnabled && (!data1[1] || (data1[1] && data1[1].length === 0))) {
            queryFailed = true;
            unstable_batchedUpdates(() => {
              setIsComparedToEnabled(false);
              setLoadState(LoadingStateNoData);
              setCardsLoadState(LoadingStateNoData);
              setChartLoadState(LoadingStateNoData);
              setCompChartLoadState(LoadingStateNoData);
              setSnackbar({
                message:
                  'There is no compare data available yet for this period. The dashboard will reload without compare mode enabled.',
                error: false,
                warning: false,
              });
            });
            // check max date queries, if they didn't return a date, try turning off mergeLiveData and running again
          }
        }
        if (queryFailed) {
          // track events for mixpanel
          trackEvent(
            'IAS Publisher Dashboard - Run Agg Query',
            +graph_id.replace(/\D/g, ''),
            start_date,
            end_date,
            isComparedToEnabled ? comp_start_date : '',
            isComparedToEnabled ? comp_end_date : '',
            cfg.timezone,
            queryTotalTime,
            true,
            query_duration_days,
            isComparedToEnabled ? comp_query_duration_days : 0,
            isComparedToEnabled,
          );
          return;
        }

        // track events for mixpanel
        trackEvent(
          'IAS Publisher Dashboard - Run Agg Query',
          +graph_id.replace(/\D/g, ''),
          start_date,
          end_date,
          isComparedToEnabled ? comp_start_date : '',
          isComparedToEnabled ? comp_end_date : '',
          cfg.timezone,
          queryTotalTime,
          false,
          query_duration_days,
          isComparedToEnabled ? comp_query_duration_days : 0,
          isComparedToEnabled,
        );

        // TODO: investigate if it's better to setChartLoadStates to noData if empty array
        unstable_batchedUpdates(() => {
          setData([data1[0] ? data1[0] : [], data1[1] ? data1[1] : []]);
          setLoadState(LoadingStateHasData);
          setCardsLoadState(LoadingStateNoData);
          setChartLoadState(LoadingStateHasData);
          setCompChartLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        // track events for mixpanel
        const queryTotalTime = moment().unix() - queryStartTime;
        trackEvent(
          'IAS Publisher Dashboard - Run Agg Query',
          +graph_id.replace(/\D/g, ''),
          start_date,
          end_date,
          isComparedToEnabled ? comp_start_date : '',
          isComparedToEnabled ? comp_end_date : '',
          cfg.timezone,
          queryTotalTime,
          true,
          query_duration_days,
          isComparedToEnabled ? comp_query_duration_days : 0,
          isComparedToEnabled,
        );
        setLoadState(LoadingStateNoData);
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setChartLoadState(LoadingStateNoData);
          setCompChartLoadState(LoadingStateNoData);
          setCardsLoadState(LoadingStateNoData);
          setSnackbar({
            message: 'Something went wrong: Please try again.',
            error: true,
            warning: false,
          });
        });
      });
  };

  const configureData = (allData: any): IMeasurementDataItem[] => {
    const data = [] as IMeasurementDataItem[];

    const calData = allData[0] || [];
    const compData = allData.length === 2 ? allData[1] : [];

    let offset = 0;
    let offsetComp = 0;
    if (densityRangeAgg === DateGroupHours) {
      offset = moment(calendar.endDate).utcOffset() - moment(calendar.startDate).utcOffset();
      offsetComp =
        moment(compareCalendar.endDate).utcOffset() - moment(compareCalendar.startDate).utcOffset();
    }
    // only handle the special case when summer -> winter when sunday has two 1am-2am
    let currentCalDate: moment.Moment =
      offset === -60
        ? moment(calendar.startDate).add(offset, 'minute')
        : moment(calendar.startDate);
    let currentCompDate: moment.Moment =
      offsetComp === -60
        ? moment(compareCalendar.startDate).add(offset, 'minute')
        : moment(compareCalendar.startDate);

    // calTotal, compTotal represents the difference between the start and end dates in the value of the densityRange (days, hours, weeks, months)
    let calTotal = 0,
      compTotal = 0;
    let calDataIndex: number = 0,
      compDataIndex: number = 0;

    if (densityRangeAgg === DateGroupDays) {
      calTotal = moment(calendar.endDate).diff(currentCalDate, 'days') + 1;
      compTotal = moment(compareCalendar.endDate).diff(currentCompDate, 'days') + 1;
    } else if (densityRangeAgg === DateGroupHours) {
      calTotal = moment(calendar.endDate).diff(currentCalDate, 'hours') + 1;
      compTotal = moment(compareCalendar.endDate).diff(currentCompDate, 'hours') + 1;
    } else if (densityRangeAgg === DateGroupWeeks) {
      calTotal =
        moment(calendar.endDate)
          .endOf('week')
          .diff(moment(currentCalDate).startOf('week'), 'weeks') + 1;
      compTotal =
        moment(compareCalendar.endDate)
          .endOf('week')
          .diff(moment(currentCompDate).startOf('week'), 'weeks') + 1;
    } else if (densityRangeAgg === DateGroupMonths) {
      calTotal =
        moment(calendar.endDate)
          .endOf('month')
          .diff(moment(currentCalDate).startOf('month'), 'months') + 1;
      compTotal =
        moment(compareCalendar.endDate)
          .endOf('month')
          .diff(moment(currentCompDate).startOf('month'), 'months') + 1;
    }
    const numItems = isComparedToEnabled ? Math.max(calTotal, compTotal) : calTotal;
    for (let i = 0; i < numItems; i++) {
      // create a data item from blankDataItem and assign the date
      const currentDataItem = Object.assign({}, blankMeasurementDataItem);
      assignDateLabel(currentDataItem, i, densityRangeAgg);
      // if the data1 has the index, the index is in range of the difference in date and the dates match
      // fill out the data for the new data item

      if (
        calData[calDataIndex] &&
        calDataIndex < calTotal &&
        dateMatches(densityRangeAgg, moment(calData[calDataIndex].Date).tz('UTC'), currentCalDate)
      ) {
        for (const value of Object.values(sectionMetrics)) {
          const key = value.dataKey as IIASFirstAndThirdPartyDataKey;
          currentDataItem[key] = calData[calDataIndex][key];
        }
        calDataIndex++;
      }

      // if the data2 (compare) has the index, the index is in range of the difference in date and the dates match
      // fill out the data for the new data item
      if (
        compData[compDataIndex] &&
        isComparedToEnabled &&
        compDataIndex < compTotal &&
        dateMatches(
          densityRangeAgg,
          moment(compData[compDataIndex].Date).tz('UTC'),
          currentCompDate,
        )
      ) {
        for (const value of Object.values(sectionMetrics)) {
          const compareKey = value.compareKey as IIASFirstAndThirdPartyCompareDataKey;
          const key = value.dataKey as IIASFirstAndThirdPartyDataKey;
          currentDataItem[compareKey] = compData[compDataIndex][key];
        }
        compDataIndex++;
      }

      // assign date values to the data item and increment the current dates
      if (densityRangeAgg === DateGroupHours) {
        currentDataItem.CDate = formatHourDayMonth(currentCompDate);
        currentDataItem.Date = formatHourDayMonth(currentCalDate);
        currentCalDate = currentCalDate.add(1, 'hours');
        currentCompDate = currentCompDate.add(1, 'hours');
      } else {
        currentDataItem.CDate = formatDayMonthName(currentCompDate);
        currentDataItem.Date = formatDayMonthName(currentCalDate);
        if (densityRangeAgg === DateGroupDays) {
          currentCalDate = currentCalDate.add(1, 'days');
          currentCompDate = currentCompDate.add(1, 'days');
        } else if (densityRangeAgg === DateGroupWeeks) {
          currentCalDate = currentCalDate.add(1, 'weeks');
          currentCompDate = currentCompDate.add(1, 'weeks');
        } else if (densityRangeAgg === DateGroupMonths) {
          currentCalDate = currentCalDate.add(1, 'months');
          currentCompDate = currentCompDate.add(1, 'months');
        }
      }
      // in dst change day, there is an unneeded empty data item inserted, which needs to be removed
      if (offset === -60) {
        if (data[data.length - 1] && currentDataItem.Date === data[data.length - 1].Date) {
          continue;
        }
      }
      data.push(currentDataItem);
    }

    return data;
  };

  // filter pill functions
  const handleFilterOnChange = (menuTitle: string) => (values: IMenuList) => {
    setHandleType('handleFilterOnChange');
    const clonedFilters = cloneDeep(filters);
    clonedFilters[menuTitle].list = values;
    setFilters(clonedFilters);
  };

  const handleFilterAccept = () => {
    setHandleType('handleFilterAccept');
    runAggregateQuery({});
  };

  const handleClearFilters = () => {
    setHandleType('handleClearFilters');
    const clonedFilters = cloneDeep(filters);
    Object.values(clonedFilters).forEach((filter) => {
      Object.values(filter.list).forEach((item) => {
        item.selected = false;
      });
    });
    setFilters(clonedFilters);
  };

  // tab functions
  const handleTabsOnChange = (
    // @ts-ignore
    e: React.SyntheticEvent,
    tabNumber: number,
  ) => {
    const newSection = getSectionInfo(tabNumber, cfg.tabIndex);
    setHandleType('handleTabsOnChange');
    setCurrentTab(tabNumber);
    setCurrentSection(newSection);
    trackEvent(`IAS Publisher Dashboard - Click ${newSection.label} Tab` as DashboardEventTypes);
  };

  // card functions
  const handleClickCard = (key: string) => {
    setHandleType('handleClickCard');
    const clonedActiveMetrics = cloneDeep(activeMetrics);
    clonedActiveMetrics[key] = !activeMetrics[key];
    setActiveMetrics(clonedActiveMetrics);
  };

  const handleClickSelectableCard = (dataKey: string, index: number) => {
    setHandleType('handleClickSelectableCard');
    const clonedActiveMetrics = cloneDeep(activeMetrics);
    clonedActiveMetrics[dataKey] = !activeMetrics[dataKey];
    setActiveMetrics(clonedActiveMetrics);

    if (index >= 0) {
      setCards((prev) => {
        const cards = [...prev];
        cards[index].isActive = !cards[index].isActive;
        return cards;
      });
    }
  };

  const handleSwitchCardSelected = (newkey: string, index: number): void => {
    const currentSectionInfo =
      currentSection.cardMetrics.find((cardMetric) => {
        return cardMetric.key === newkey;
      }) || ({} as IIASDashboardSectionCardMetricInfo);
    const metric = {} as Record<string, IIASDashboardMetricInfo>;
    for (const [key, val] of Object.entries(sectionMetrics)) {
      if (currentSectionInfo.key === key) {
        metric[key] = val;
      }
    }
    if (currentSectionInfo) {
      const newCard = generateCard(
        sectionMetrics,
        totals,
        isComparedToEnabled,
        colorBank,
        newkey,
        index,
      );

      const clonedActiveMetrics = cloneDeep(activeMetrics);
      clonedActiveMetrics[cards[index].dataKey] = false;
      clonedActiveMetrics[newCard.dataKey] = true;
      setActiveMetrics(clonedActiveMetrics);

      setCards((prev) => {
        const cards = [...prev];
        cards[index] = newCard;
        cards[index].isActive = true;
        cards[index].color = colorBank[index % colorBank.length];
        return cards;
      });

      setDisplayedCardsKeysAndColors((prev) => {
        const newKeysAndColors = [...prev];
        newKeysAndColors[index].dataKey = newCard.dataKey;
        return newKeysAndColors;
      });
    }
  };

  // calendar functions
  const handleChangeAggRangeSelected = (
    rangeSelected: string,
    isCompareCalendar: boolean,
    applied?: boolean,
  ): void => {
    setHandleType('handleChangeAggRangeSelected');
    if (isCompareCalendar) {
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      if (applied) {
        clonedCompareCalendar.rangeSelected = rangeSelected;
        clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
      } else {
        clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
      }
      setCompareCalendar(clonedCompareCalendar);
    } else {
      const clonedCalendar = cloneDeep(calendar);
      if (applied) {
        clonedCalendar.rangeSelected = rangeSelected;
        clonedCalendar.rangeDisplaySelected = rangeSelected;
      } else {
        clonedCalendar.rangeDisplaySelected = rangeSelected;
      }
      unstable_batchedUpdates(() => {
        setRangeSelectedStr(rangeSelected);
        setCalendar(clonedCalendar);
      });
    }
  };

  // this causes a console error due to a bad setState within the nested component
  // no fix needed for now
  const handleCalendarDateChange = (
    isCompareCalendar: boolean,
  ): ((start: moment.Moment, end: moment.Moment, isCustom: boolean) => void) => {
    setHandleType('handleCalendarDateChange');
    if (isCompareCalendar) {
      return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
        if (isCustom) {
          handleChangeAggRangeSelected('Custom', true, true);
        }
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        clonedCompareCalendar.displayStartDate = start;
        clonedCompareCalendar.displayEndDate = end;
        setCompareCalendar(clonedCompareCalendar);
      };
    } else {
      return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
        if (isCustom) {
          handleChangeAggRangeSelected('Custom', false, true);
        }
        const clonedCalendar = cloneDeep(calendar);
        clonedCalendar.displayStartDate = start;
        clonedCalendar.displayEndDate = end;
        setCalendar(clonedCalendar);
      };
    }
  };

  const handleDateRangeChange = (
    start: moment.Moment,
    end: moment.Moment,
    isCompareCalendar: boolean,
  ): void => {
    setHandleType('handleDateRangeChange');
    const startDate = cloneDeep(start).startOf('day');
    const endDate = cloneDeep(end).endOf('day');

    if (isCompareCalendar) {
      const clonedCompareCalendar = cloneDeep(compareCalendar);
      clonedCompareCalendar.startDate = startDate;
      clonedCompareCalendar.endDate = endDate;
      setCompareCalendar(clonedCompareCalendar);

      setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
    } else {
      const clonedCalendar = cloneDeep(calendar);
      clonedCalendar.startDate = startDate;
      clonedCalendar.endDate = endDate;
      setCalendar(clonedCalendar);

      setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
    }
  };

  const handleCalendarFocusedInputChange = (
    isCompareCalendar: boolean,
  ): ((focusedInput: IFocusedInput) => void) => {
    setHandleType('handleCalendarFocusedInputChange');
    if (isCompareCalendar) {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          setCalFocusedInput(null);
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;

          setCompFocusedInput(null);
          clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          setCalFocusedInput(null);
          setCompFocusedInput(focusedInput);
        }
        setCalendar(clonedCalendar);
        setCompareCalendar(clonedCompareCalendar);
      };
    } else {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          setCalFocusedInput(null);
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;

          setCompFocusedInput(null);
          clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          setCalFocusedInput(focusedInput);
          setCompFocusedInput(null);
        }
        setCalendar(clonedCalendar);
        setCompareCalendar(clonedCompareCalendar);
      };
    }
  };

  const handleCompareEnabled = () => {
    setHandleType('handleCompareEnabled');
    setIsComparedToEnabled(!isComparedToEnabled);
    trackEvent('IAS Publisher Dashboard - Compare Enabled');
  };

  const handleDensityRangeChangeAgg = (densityRangeAgg: DateGroup): void => {
    setHandleType('handleDensityRangeChangeAgg');
    const clonedCalendar = cloneDeep(calendar);
    const clonedCompareCalendar = cloneDeep(compareCalendar);
    if (densityRangeAgg === DateGroupDays) {
      setDensityRangeAgg(densityRangeAgg);
      // switch to last 7 days and last last 7 days for date ranges
      clonedCalendar.startDate = getStartDate(cfg.timezone, 6, 'day').startOf('day');
      clonedCalendar.endDate = getStartDate(cfg.timezone, 0, 'day').endOf('day');
      clonedCalendar.displayStartDate = getStartDate(cfg.timezone, 6, 'day').startOf('day');
      clonedCalendar.displayEndDate = getStartDate(cfg.timezone, 0, 'day').endOf('day');
      clonedCalendar.rangeSelected = 'Last 7 Days';
      clonedCalendar.rangeDisplaySelected = 'Last 7 Days';

      clonedCompareCalendar.startDate = getStartDate(cfg.timezone, 13, 'day').startOf('day');
      clonedCompareCalendar.endDate = getStartDate(cfg.timezone, 7, 'day').endOf('day');
      clonedCompareCalendar.displayStartDate = getStartDate(cfg.timezone, 13, 'day').startOf('day');
      clonedCompareCalendar.displayEndDate = getStartDate(cfg.timezone, 7, 'day').endOf('day');
      setCalendar(clonedCalendar);
      setCompareCalendar(clonedCompareCalendar);
    } else if (densityRangeAgg === DateGroupWeeks) {
      const daysFromSunday = moment().tz(cfg.timezone).weekday();
      setDensityRangeAgg(densityRangeAgg);
      // switch to this week and last week
      clonedCalendar.startDate = getStartDate(cfg.timezone, daysFromSunday, 'day').startOf('day');
      clonedCalendar.endDate = getStartDate(cfg.timezone, 0, 'day').endOf('day');
      clonedCalendar.displayStartDate = getStartDate(cfg.timezone, daysFromSunday, 'day').startOf(
        'day',
      );
      clonedCalendar.displayEndDate = getStartDate(cfg.timezone, 0, 'day').endOf('day');
      clonedCalendar.rangeSelected = 'Last Week (Sun-Sat)';
      clonedCalendar.rangeDisplaySelected = 'Last Week (Sun-Sat)';

      clonedCompareCalendar.startDate = getStartDate(
        cfg.timezone,
        daysFromSunday + 7,
        'day',
      ).startOf('day');
      clonedCompareCalendar.endDate = getStartDate(cfg.timezone, daysFromSunday + 1, 'day').endOf(
        'day',
      );
      clonedCompareCalendar.displayStartDate = getStartDate(
        cfg.timezone,
        daysFromSunday + 7,
        'day',
      ).startOf('day');
      clonedCompareCalendar.displayEndDate = getStartDate(
        cfg.timezone,
        daysFromSunday + 1,
        'day',
      ).endOf('day');
      setCalendar(clonedCalendar);
      setCompareCalendar(clonedCompareCalendar);
    } else if (densityRangeAgg === DateGroupMonths) {
      setDensityRangeAgg(densityRangeAgg);
      // switch to this month and last month
      clonedCalendar.startDate = getStartDate(cfg.timezone, 0, 'day').startOf('month');
      clonedCalendar.endDate = getStartDate(cfg.timezone, 0, 'day').endOf('day');
      clonedCalendar.displayStartDate = getStartDate(cfg.timezone, 0, 'day').startOf('month');
      clonedCalendar.displayEndDate = getStartDate(cfg.timezone, 0, 'day').endOf('day');
      clonedCalendar.rangeSelected = 'Last Month';
      clonedCalendar.rangeDisplaySelected = 'Last Month';

      clonedCompareCalendar.startDate = getStartDate(cfg.timezone, 0, 'day')
        .startOf('month')
        .add(-1, 'months');
      clonedCompareCalendar.endDate = getStartDate(cfg.timezone, 0, 'day')
        .endOf('month')
        .add(-1, 'months');
      clonedCompareCalendar.displayStartDate = getStartDate(cfg.timezone, 0, 'day')
        .startOf('month')
        .add(-1, 'months');
      clonedCompareCalendar.displayEndDate = getStartDate(cfg.timezone, 0, 'day')
        .endOf('month')
        .add(-1, 'months');
      setCalendar(clonedCalendar);
      setCompareCalendar(clonedCompareCalendar);
    } else {
      setDensityRangeAgg(densityRangeAgg);
    }
  };

  // snackbar function
  const handleSnackbarClose = () => {
    setSnackbar({
      message: '',
      error: false,
      warning: false,
    });
  };

  const isLoading = isComparedToEnabled
    ? cardsLoadState === LoadingStateLoading ||
      chartLoadState === LoadingStateLoading ||
      compChartLoadState === LoadingStateLoading
    : cardsLoadState === LoadingStateLoading || chartLoadState === LoadingStateLoading;

  return (
    <Panels maxWidth vertical relative minWidthMain>
      <Panel
        largeHorizontalPadding
        verticalPadding
        paddingLeft='72px'
        style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
      >
        <ModelTopBar
          title={routeName(pathname)}
          session={session}
          user={user}
          publisher={session.publisher}
          classes={classes}
          delegateElevate={true}
          currentPath={pathname}
          disableTitlePaddingLeft
        />
      </Panel>
      <Panel scroll largeHorizontalPadding>
        <Grid item style={{ marginBottom: '4px' }}>
          <IASDashboardTabs
            currentTab={currentTab}
            // @ts-ignore
            onChange={handleTabsOnChange}
            tabs={extractLabels(cfg.sections)}
            value={currentTab}
          />
        </Grid>
        <Grid item>
          <Divider
            variant='fullWidth'
            style={{ height: '1px', marginTop: '3px', marginBottom: '3px' }}
            orientation='horizontal'
          />
        </Grid>
        {/* FILTERS SECTION */}
        <Grid item>
          <Paper className={classes.paper} style={{ marginTop: 8, marginBottom: 8 }}>
            <IASDashboardFilters
              session={session}
              timezone={cfg.timezone}
              // calendar props
              range1Selected={rangeSelectedStr}
              range1DisplaySelected={rangeSelectedStr}
              calStartDate={calendar.startDate}
              calEndDate={calendar.endDate}
              calDisplayStartDate={calendar.displayStartDate}
              calDisplayEndDate={calendar.displayEndDate}
              calFocusedInput={calFocusedInput}
              // compare calendar props
              range2Selected={compareCalendar.rangeSelected}
              range2DisplaySelected={compareCalendar.rangeDisplaySelected}
              compStartDate={compareCalendar.startDate}
              compEndDate={compareCalendar.endDate}
              compDisplayStartDate={compareCalendar.displayStartDate}
              compDisplayEndDate={compareCalendar.displayEndDate}
              compFocusedInput={compFocusedInput}
              // other calendar props
              isComparedToEnabled={isComparedToEnabled}
              handleCompareEnabled={handleCompareEnabled}
              // calendar functions
              handleChangeAggRangeSelected={handleChangeAggRangeSelected}
              handleCalendarDateChange={handleCalendarDateChange}
              handleDateRangeChange={handleDateRangeChange}
              handleCalendarFocusedInputChange={handleCalendarFocusedInputChange}
              // filters
              filters={Object.values(filters)}
              handleFilterOnChange={handleFilterOnChange}
              handleFilterAccept={handleFilterAccept}
              // track event
              trackEvent={trackEvent}
              handleClearFilters={handleClearFilters}
            />
          </Paper>
        </Grid>
        {isLoading && (
          <Grid item>
            <LinearProgress style={{ width: '100%', height: 4 }} />
          </Grid>
        )}
        <Grid
          item
          container
          direction='column'
          style={{ marginTop: 8, marginBottom: 8 }}
          spacing={2}
        >
          {/* CARDS SECTION */}
          {currentSection.areCardsSelectable ? (
            <Grid item>
              <IASDashboardSelectableCards
                cards={cards}
                isComparedToEnabled={isComparedToEnabled}
                activeMetrics={activeMetrics}
                handleClickSelectableCard={handleClickSelectableCard}
                handleSwitchCardSelected={handleSwitchCardSelected}
                loadStatus={cardsLoadState}
                colors={cfg.colors}
                allMetrics={getAllSelectableCardMetricsInfo(currentTab, cfg.tabIndex)}
              />
            </Grid>
          ) : (
            <Grid item>
              <IASDashboardCards
                cards={cards}
                isComparedToEnabled={isComparedToEnabled}
                handleClickCard={handleClickCard}
                loadStatus={cardsLoadState}
                activeMetrics={activeMetrics}
              />
            </Grid>
          )}
          {/* CHARTS SECTION */}
          <Grid item style={{ marginTop: 8, marginBottom: 8 }}>
            <IASDashboardCharts
              classes={classes}
              colors={cfg.colors}
              loadState={loadState}
              chartLoadState={chartLoadState}
              compChartLoadState={compChartLoadState}
              data={mergedData}
              handleDensityRangeChange={handleDensityRangeChangeAgg}
              calStartDate={calendar.startDate}
              calEndDate={calendar.endDate}
              densityRangeAgg={densityRangeAgg}
              compStartDate={compareCalendar.startDate}
              compEndDate={compareCalendar.endDate}
              isComparedToEnabled={isComparedToEnabled}
              metrics={sectionMetrics}
              activeMetrics={activeMetrics}
              displayedCardsKeysAndColors={displayedCardsKeysAndColors}
              areCardsSelectable={currentSection.areCardsSelectable}
              // specific to secondary charts
              queryParams={queryParams}
              secondaryCharts={currentSection.secondaryCharts}
            />
          </Grid>
          {/* TABLE SECTION */}
          {currentSection.table && (
            <Grid item>
              <IASDashboardTable
                classes={classes}
                tableInfo={currentSection.table}
                queryParams={queryParams}
              />
            </Grid>
          )}
        </Grid>
        <SnackbarView
          autoHideDuration={12000}
          open={!!snackbar.message}
          error={!!snackbar.error}
          onClose={handleSnackbarClose}
          messageColor={''}
          backgroundColor={''}
          closeColor={''}
          warning={!!snackbar.warning}
        >
          {snackbar.message}
        </SnackbarView>
      </Panel>
    </Panels>
  );
};

export const IASDashboard = decorateV2<Partial<ISessionState>>(cfg.defaultProps, _IASDashboard);
